'use client';

import React, { useState } from 'react';

interface PayoutMethod {
  id: string;
  type: 'bank' | 'mobile_money';
  label: string;
  details: string;
  isDefault: boolean;
  status: 'verified' | 'pending' | 'failed';
  addedDate: string;
  provider?: string;
}

interface BankFormData {
  accountName: string;
  accountNumber: string;
  bankName: string;
  branchCode: string;
  swiftCode: string;
}

interface MobileFormData {
  provider: string;
  phoneNumber: string;
  accountName: string;
}

const initialMethods: PayoutMethod[] = [
  {
    id: 'pm-001',
    type: 'mobile_money',
    label: 'MTN Mobile Money',
    details: '+256 77X XXX X89',
    isDefault: true,
    status: 'verified',
    addedDate: 'Mar 12, 2026',
    provider: 'MTN',
  },
  {
    id: 'pm-002',
    type: 'bank',
    label: 'Stanbic Bank',
    details: '•••• •••• 4821',
    isDefault: false,
    status: 'verified',
    addedDate: 'Jan 05, 2026',
  },
];

const mobileProviders = ['MTN Mobile Money', 'Airtel Money', 'M-Pesa', 'Tigo Pesa', 'Orange Money', 'Vodacom M-Pesa'];
const bankNames = ['Stanbic Bank', 'Equity Bank', 'KCB Bank', 'Absa Bank', 'Standard Chartered', 'Centenary Bank', 'DFCU Bank', 'Post Bank'];

const statusConfig: Record<string, { label: string; color: string; dot: string }> = {
  verified: { label: 'Verified', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  pending: { label: 'Pending', color: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  failed: { label: 'Failed', color: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
};

export default function PayoutMethods() {
  const [methods, setMethods] = useState<PayoutMethod[]>(initialMethods);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState<'bank' | 'mobile_money'>('mobile_money');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [bankForm, setBankForm] = useState<BankFormData>({
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchCode: '',
    swiftCode: '',
  });

  const [mobileForm, setMobileForm] = useState<MobileFormData>({
    provider: '',
    phoneNumber: '',
    accountName: '',
  });

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const handleSetDefault = (id: string) => {
    setMethods((prev) => prev.map((m) => ({ ...m, isDefault: m.id === id })));
    showSuccess('Default payout method updated.');
  };

  const handleDelete = (id: string) => {
    setMethods((prev) => prev.filter((m) => m.id !== id));
    setDeleteConfirmId(null);
    showSuccess('Payout method removed.');
  };

  const handleAddMethod = () => {
    if (addType === 'bank') {
      if (!bankForm.accountName || !bankForm.accountNumber || !bankForm.bankName) return;
      const newMethod: PayoutMethod = {
        id: `pm-${Date.now()}`,
        type: 'bank',
        label: bankForm.bankName,
        details: `•••• •••• ${bankForm.accountNumber.slice(-4)}`,
        isDefault: methods.length === 0,
        status: 'pending',
        addedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
      };
      setMethods((prev) => [...prev, newMethod]);
    } else {
      if (!mobileForm.provider || !mobileForm.phoneNumber || !mobileForm.accountName) return;
      const newMethod: PayoutMethod = {
        id: `pm-${Date.now()}`,
        type: 'mobile_money',
        label: mobileForm.provider,
        details: mobileForm.phoneNumber,
        isDefault: methods.length === 0,
        status: 'pending',
        addedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
        provider: mobileForm.provider.split(' ')[0],
      };
      setMethods((prev) => [...prev, newMethod]);
    }
    setShowAddModal(false);
    setBankForm({ accountName: '', accountNumber: '', bankName: '', branchCode: '', swiftCode: '' });
    setMobileForm({ provider: '', phoneNumber: '', accountName: '' });
    showSuccess('Payout method added. Verification may take 1–2 business days.');
  };

  const isAddFormValid = addType === 'bank'
    ? !!(bankForm.accountName && bankForm.accountNumber && bankForm.bankName)
    : !!(mobileForm.provider && mobileForm.phoneNumber && mobileForm.accountName);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            Payout Methods
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage where your earnings are sent after each booking</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          <span className="text-base leading-none">+</span>
          Add Method
        </button>
      </div>

      {/* Success Toast */}
      {successMsg && (
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm font-medium">
          <span className="text-base">✓</span>
          {successMsg}
        </div>
      )}

      {/* Info Banner */}
      <div className="flex items-start gap-3 px-4 py-3.5 bg-blue-50 border border-blue-100 rounded-xl">
        <span className="text-blue-500 text-lg mt-0.5">ℹ</span>
        <p className="text-sm text-blue-700">
          Payouts are processed within <strong>2–5 business days</strong> after guest checkout. Your default method is used for all automatic payouts.
        </p>
      </div>

      {/* Methods List */}
      {methods.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center text-3xl mb-4">💳</div>
          <p className="text-gray-700 font-semibold text-base">No payout methods yet</p>
          <p className="text-gray-400 text-sm mt-1">Add a bank account or mobile money to receive your earnings</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-5 px-5 py-2.5 bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            Add Payout Method
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {methods.map((method) => {
            const sc = statusConfig[method.status];
            return (
              <div
                key={method.id}
                className={`bg-white rounded-2xl border shadow-sm p-5 transition-all ${method.isDefault ? 'border-rose-200 ring-1 ring-rose-100' : 'border-gray-100'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Icon + Info */}
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${method.type === 'mobile_money' ? 'bg-amber-50' : 'bg-blue-50'}`}>
                      {method.type === 'mobile_money' ? '📱' : '🏦'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                          {method.label}
                        </span>
                        {method.isDefault && (
                          <span className="px-2 py-0.5 bg-rose-100 text-rose-600 text-xs font-semibold rounded-full">Default</span>
                        )}
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border ${sc.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm mt-0.5">{method.details}</p>
                      <p className="text-gray-400 text-xs mt-0.5">Added {method.addedDate}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!method.isDefault && (
                      <button
                        onClick={() => handleSetDefault(method.id)}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                      >
                        Set Default
                      </button>
                    )}
                    {deleteConfirmId === method.id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500">Remove?</span>
                        <button
                          onClick={() => handleDelete(method.id)}
                          className="px-2.5 py-1.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-2.5 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(method.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove method"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Payout Schedule Info */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-semibold text-gray-900 text-sm mb-4" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          Payout Schedule
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: '📆', title: 'Automatic Payouts', desc: 'Triggered 24 hours after guest check-in' },
            { icon: '⏱️', title: 'Processing Time', desc: '2–5 business days to reach your account' },
            { icon: '💱', title: 'Currency', desc: 'Paid in UGX — no conversion fees' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 p-3.5 bg-gray-50 rounded-xl">
              <span className="text-xl">{item.icon}</span>
              <div>
                <p className="text-xs font-semibold text-gray-700">{item.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Method Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                Add Payout Method
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Type Selector */}
              <div className="grid grid-cols-2 gap-2 bg-gray-50 p-1.5 rounded-xl">
                {(['mobile_money', 'bank'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setAddType(t)}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      addType === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <span>{t === 'mobile_money' ? '📱' : '🏦'}</span>
                    {t === 'mobile_money' ? 'Mobile Money' : 'Bank Account'}
                  </button>
                ))}
              </div>

              {/* Mobile Money Form */}
              {addType === 'mobile_money' && (
                <div className="space-y-3.5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Mobile Money Provider</label>
                    <select
                      value={mobileForm.provider}
                      onChange={(e) => setMobileForm((p) => ({ ...p, provider: e.target.value }))}
                      className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 bg-white text-gray-900"
                    >
                      <option value="">Select provider</option>
                      {mobileProviders.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      placeholder="+256 7XX XXX XXX"
                      value={mobileForm.phoneNumber}
                      onChange={(e) => setMobileForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                      className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Account Name</label>
                    <input
                      type="text"
                      placeholder="Name registered on mobile money"
                      value={mobileForm.accountName}
                      onChange={(e) => setMobileForm((p) => ({ ...p, accountName: e.target.value }))}
                      className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 placeholder-gray-400"
                    />
                  </div>
                </div>
              )}

              {/* Bank Account Form */}
              {addType === 'bank' && (
                <div className="space-y-3.5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Bank Name</label>
                    <select
                      value={bankForm.bankName}
                      onChange={(e) => setBankForm((p) => ({ ...p, bankName: e.target.value }))}
                      className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 bg-white text-gray-900"
                    >
                      <option value="">Select bank</option>
                      {bankNames.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Account Name</label>
                    <input
                      type="text"
                      placeholder="Name on bank account"
                      value={bankForm.accountName}
                      onChange={(e) => setBankForm((p) => ({ ...p, accountName: e.target.value }))}
                      className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Account Number</label>
                    <input
                      type="text"
                      placeholder="Enter account number"
                      value={bankForm.accountNumber}
                      onChange={(e) => setBankForm((p) => ({ ...p, accountNumber: e.target.value }))}
                      className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 placeholder-gray-400"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Branch Code <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        placeholder="e.g. 001"
                        value={bankForm.branchCode}
                        onChange={(e) => setBankForm((p) => ({ ...p, branchCode: e.target.value }))}
                        className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">SWIFT Code <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        placeholder="e.g. STBAUGKA"
                        value={bankForm.swiftCode}
                        onChange={(e) => setBankForm((p) => ({ ...p, swiftCode: e.target.value }))}
                        className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Security Note */}
              <div className="flex items-start gap-2.5 px-3.5 py-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 text-sm mt-0.5">🔒</span>
                <p className="text-xs text-gray-500">Your financial details are encrypted and stored securely. Verification takes 1–2 business days.</p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMethod}
                disabled={!isAddFormValid}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-colors"
              >
                Add Method
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
